import { Component } from 'react'
import { isRouteErrorResponse, useRouteError, Link, useNavigate } from 'react-router-dom'

function RouteErrorFallback() {
  const error = useRouteError()

  let title = 'Something went wrong'
  let message = 'An unexpected error occurred.'
  let status: number | undefined

  if (isRouteErrorResponse(error)) {
    status = error.status
    if (error.status === 404) {
      title = 'Page not found'
      message = "The page you're looking for doesn't exist."
    } else {
      message = error.statusText || message
    }
  } else if (error instanceof Error) {
    message = error.message
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="mx-auto max-w-md text-center px-4">
        {status && (
          <p className="text-5xl font-bold text-muted-foreground mb-4">{status}</p>
        )}
        <h1 className="text-2xl font-semibold mb-2">{title}</h1>
        <p className="text-muted-foreground mb-6">{message}</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/hub"
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}

/**
 * Hub-level error fallback — keeps sidebar/header visible via the HubLayout parent.
 * Renders inside the main content area only.
 */
export function HubErrorFallback() {
  const error = useRouteError()
  const navigate = useNavigate()

  let title = 'Something went wrong'
  let message = 'An unexpected error occurred in this section.'

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found'
      message = "The page you're looking for doesn't exist."
    } else {
      message = error.statusText || message
    }
  } else if (error instanceof Error) {
    message = error.message
  }

  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="mx-auto max-w-md text-center px-4">
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-muted-foreground mb-4">{message}</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => navigate(0)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/hub"
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}

/**
 * Project-level error fallback — keeps project nav visible via the ProjectLayout parent.
 * Renders inside the project content area only.
 */
export function ProjectErrorFallback() {
  const error = useRouteError()
  const navigate = useNavigate()

  let title = 'Something went wrong'
  let message = 'An error occurred while loading this feature.'

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Feature not found'
      message = "This feature doesn't exist or isn't available."
    } else {
      message = error.statusText || message
    }
  } else if (error instanceof Error) {
    message = error.message
  }

  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="mx-auto max-w-md text-center px-4">
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-muted-foreground mb-4">{message}</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => navigate(0)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  )
}

interface Props {
  children?: React.ReactNode
  fallback?: React.ReactNode
  level?: 'app' | 'hub' | 'project'
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundaryClass extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  private resetError = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      const level = this.props.level ?? 'app'
      const errorMessage = this.state.error?.message ?? 'An unexpected error occurred.'

      if (level === 'app') {
        return (
          <div className="flex min-h-screen items-center justify-center">
            <div className="mx-auto max-w-md text-center px-4">
              <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>
              <p className="text-muted-foreground mb-6">{errorMessage}</p>
              <button
                onClick={() => window.location.reload()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Reload page
              </button>
            </div>
          </div>
        )
      }

      if (level === 'hub') {
        return (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="mx-auto max-w-md text-center px-4">
              <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
              <p className="text-muted-foreground mb-4">{errorMessage}</p>
              <button
                onClick={this.resetError}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Try again
              </button>
            </div>
          </div>
        )
      }

      // project level
      return (
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="mx-auto max-w-md text-center px-4">
            <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">{errorMessage}</p>
            <button
              onClick={this.resetError}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Try again
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Default export for React Router errorElement (top-level)
export function ErrorBoundary() {
  return <RouteErrorFallback />
}
